import React, { FC, useState } from 'react';

import { EvSegmentedButtons } from '@evinced-private/ui-common';

import { PropertyHelper } from '../../../helpers/PropertyHelper';
import { getSourceDisplayText, isPropertyInListMode } from '../../../helpers/PropertySourceHelper';
import { IProperty } from '../../../interfaces/Property';
import { useNotifications } from '../../../providers/notificationsProvider/NotificationsConsumer';
import { logger } from '../../../services/Logger';
import { isCrawlCurrentlyRunning } from '../../../services/PropertiesService';
import { URL_SET_SOURCES } from '../../../types/UrlSetSources';
import { ChangePropertySourceConfirmPopup } from '../ChangePropertySourceConfirmPopup';

const SOURCE_CRAWL = 'crawl';
const SOURCE_LIST = 'list';

const SOURCE_OPTIONS = [
	{ label: 'Crawl', id: SOURCE_CRAWL },
	{ label: 'List', id: SOURCE_LIST }
];

interface IPropertySettingsSourcePickerProps {
	sourceMode: URL_SET_SOURCES;
	originalProperty: IProperty;
	property: IProperty;
	setLoading: (state: boolean) => void;
	getPropertyData: (sourceMode: URL_SET_SOURCES) => Promise<void>;
	setPropertySourceMode: (source: URL_SET_SOURCES) => void;
}

export const PropertySettingsSourcePicker: FC<IPropertySettingsSourcePickerProps> = ({
	sourceMode,
	originalProperty,
	property,
	setLoading,
	getPropertyData,
	setPropertySourceMode
}: IPropertySettingsSourcePickerProps) => {
	const [isPopupLoading, setIsPopupLoading] = useState<boolean>(false);
	const [isPopupOpen, setPopupOpen] = useState(false);
	const [isCrawlRunning, setIsCrawlRunning] = useState<boolean>(false);
	const notificationsContext = useNotifications();
	const isInListMode = isPropertyInListMode(sourceMode);

	const updateIsCrawlRunningState = (): Promise<void> => {
		if (!property.id) {
			// no need to check if a crawl exists becuase the property isn't created yet
			return Promise.resolve();
		}
		return isCrawlCurrentlyRunning(property.id).then((result) => {
			setIsCrawlRunning(result);
		});
	};

	const onChangeToDiscoveryMode = (): void => {
		const shouldOpenConfirmPopup = !PropertyHelper.isPropertyEmpty(property, isInListMode);

		if (shouldOpenConfirmPopup) {
			setPopupOpen(true);
		} else {
			setLoading(true);
			setPropertySourceMode(URL_SET_SOURCES.URL_DISCOVERY);
		}
	};

	const onChangeToListMode = (): void => {
		const shouldOpenConfirmPopup = !PropertyHelper.isPropertyEmpty(property, isInListMode);

		if (!shouldOpenConfirmPopup && !isCrawlRunning) {
			setLoading(true);
			setPropertySourceMode(URL_SET_SOURCES.UPLOAD);
		} else {
			setPopupOpen(true);

			if (property.id) {
				setIsPopupLoading(true);
				updateIsCrawlRunningState()
					.then(() => {
						setIsPopupLoading(false);
					})
					.catch((err) => {
						notificationsContext.alert({
							errorMessage: 'Error while trying to change mode.',
							serverError: err
						});
					});
			}
		}
	};

	const onCrawlCanceled = async (onClose): Promise<void> => {
		getPropertyData(URL_SET_SOURCES.UPLOAD)
			.catch((err) => {
				logger.error('Error cancelling crawl', err);
			})
			.finally(() => {
				setIsCrawlRunning(false);
				onClose();
			});
	};

	const getAriaLabelForTriggerButton = (): string => {
		const { currentMode, otherMode } = getSourceDisplayText(sourceMode);
		const checkText = isInListMode ? 'uncheck' : 'check';
		return `Mapping mode: ${currentMode}, ${checkText} this to change to ${otherMode}`;
	};

	const onConfirm = (): void => {
		setLoading(true);
		setPropertySourceMode(isInListMode ? URL_SET_SOURCES.URL_DISCOVERY : URL_SET_SOURCES.UPLOAD);
		setPopupOpen(false);
	};

	return (
		<>
			<div className="property-source-selector">
				<div className="new-label">Mapping via</div>
				<EvSegmentedButtons
					ariaLabel={getAriaLabelForTriggerButton()}
					options={SOURCE_OPTIONS}
					isControlled
					value={[isInListMode ? SOURCE_LIST : SOURCE_CRAWL]}
					onPress={(pressed) => {
						if (pressed[0] === SOURCE_CRAWL && isInListMode) {
							onChangeToDiscoveryMode();
						} else if (pressed[0] === SOURCE_LIST && !isInListMode) {
							onChangeToListMode();
						}
					}}
				/>
			</div>
			<ChangePropertySourceConfirmPopup
				originalProperty={originalProperty}
				sourceMode={sourceMode}
				isOpen={isPopupOpen}
				isCrawlRunning={isCrawlRunning}
				isPopupLoading={isPopupLoading}
				setIsOpen={setPopupOpen}
				onConfirm={onConfirm}
				onCrawlCanceled={onCrawlCanceled}
			/>
		</>
	);
};
